import { addDays, differenceInCalendarDays, parseISO, startOfDay } from 'date-fns';
import type { Goal, GoalTask, GoalTaskDurationUnit } from '../../api/types';

/** Rough length of each unit in days — must match GoalTaskDurationUnit::days(). */
export const UNIT_DAYS: Record<GoalTaskDurationUnit, number> = { day: 1, week: 7, month: 30 };

/** A deadline this close counts as "soon". */
export const SOON_DAYS = 7;

export type DeadlineState = 'overdue' | 'today' | 'soon' | 'later';

export interface GoalSchedule {
  /** Days the open jobs with an estimate add up to (done one after another). */
  remainingDays: number;
  /** Today plus remainingDays — null when no open job has an estimate. */
  estimatedFinish: Date | null;
  /** Open jobs without a duration — not counted in the estimate. */
  unestimatedJobs: number;
  /** How many days the estimated finish lands after the deadline; null when on time or unknown. */
  daysLate: number | null;
}

export const taskDays = (task: GoalTask): number | null =>
  task.duration_value != null && task.duration_unit != null
    ? task.duration_value * UNIT_DAYS[task.duration_unit]
    : null;

export function goalSchedule(goal: Goal, today: Date = new Date()): GoalSchedule {
  const openJobs = goal.tasks.filter((task) => task.type === 'do' && task.status !== 'completed');
  const estimated = openJobs.map(taskDays).filter((days): days is number => days !== null);
  const remainingDays = estimated.reduce((sum, days) => sum + days, 0);
  const estimatedFinish = estimated.length > 0 ? addDays(startOfDay(today), remainingDays) : null;
  const late =
    estimatedFinish && goal.deadline
      ? differenceInCalendarDays(estimatedFinish, parseISO(goal.deadline))
      : 0;

  return {
    remainingDays,
    estimatedFinish,
    unestimatedJobs: openJobs.length - estimated.length,
    daysLate: late > 0 ? late : null,
  };
}

/** Where an active goal's deadline stands; null without a deadline. */
export function deadlineState(goal: Goal, today: Date = new Date()): DeadlineState | null {
  if (!goal.deadline) return null;
  const days = differenceInCalendarDays(parseISO(goal.deadline), today);
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= SOON_DAYS) return 'soon';
  return 'later';
}

/**
 * A number of days in the largest unit that fits evenly — 14 → 2 weeks,
 * 60 → 2 months, 10 → 10 days.
 */
export function inLargestUnit(days: number): { value: number; unit: GoalTaskDurationUnit } {
  if (days >= UNIT_DAYS.month && days % UNIT_DAYS.month === 0) {
    return { value: days / UNIT_DAYS.month, unit: 'month' };
  }
  if (days >= UNIT_DAYS.week && days % UNIT_DAYS.week === 0) {
    return { value: days / UNIT_DAYS.week, unit: 'week' };
  }
  return { value: days, unit: 'day' };
}
